import React, {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {getStoreByName} from 'client/framework';
import {WithTooltip, Icon} from '@albert-io/atomic';
import {getModelForResourceType} from 'resources/modelRegistry';

/**
 * Determines the graded state of a snippet option.
 * @returns {'correct' | 'incorrect' | 'missed' | null}
 */
function getGradedState(isSelected, isCorrectOption) {
  if (isSelected && isCorrectOption) return 'correct';
  if (isSelected && !isCorrectOption) return 'incorrect';
  if (!isSelected && isCorrectOption) return 'missed';
  return null;
}

const TOOLTIP_LABELS = {
  correct: 'Correct',
  incorrect: 'Incorrect',
  missed: 'Missed'
};

const TOOLTIP_AUTO_SHOW_MS = 5000;

function CorrectnessIcon({gradedState}) {
  const isCorrect = gradedState === 'correct';
  return (
    <Icon
      icon={isCorrect ? 'check-circle' : 'times-circle'}
      iconStyle='regular'
      className={classnames('ml-x0-half', {
        'content-positive': isCorrect,
        'content-negative': !isCorrect
      })}
      aria-hidden='true'
    />
  );
}

CorrectnessIcon.propTypes = {
  gradedState: PropTypes.oneOf(['correct', 'incorrect', 'missed']).isRequired
};

export default function SnippetSelectionOption({
  optionId,
  storeName,
  isExplanation,
  isGuessSubmitted,
  question,
  onOptionClick,
  children,
  isBeingTranslated = false
}) {
  const [hasFocus, setHasFocus] = useState(false);
  const tooltipRef = useRef(null);
  const hasAutoShownRef = useRef(false);

  const store = getStoreByName(storeName);
  const isInteractive = !(isExplanation || store.hasResponse);
  const isSelected = store.isOptionSelected(optionId);
  const isGraded = isGuessSubmitted && !isInteractive;

  const validResponse = isGuessSubmitted
    ? // adding null/undefined checks to prevent typeError see:
      // https://app.honeybadger.io/projects/121794/faults/119696502/01K9YVVMFXZA6XJWZK806D7BTT?page=0
      question?.getMeta?.().getQuestionRubric?.().get?.('validResponse')
    : null;
  const isCorrectOption = validResponse && !validResponse.isEmpty() && validResponse.get(optionId);

  const gradedState = isGraded ? getGradedState(isSelected, isCorrectOption) : null;

  // Reset auto-show flag when graded state changes (new question)
  useEffect(() => {
    hasAutoShownRef.current = false;
  }, [gradedState]);

  // Auto-show tooltips when graded tokens scroll into view. Rules:
  // - Show tooltips for 5s when the snippet tokens become visible
  // - If user scrolls away, immediately hide tooltips
  // - Once shown once, don't show again on subsequent scrolls
  useEffect(() => {
    if (!gradedState || !tooltipRef.current) return;

    const el = tooltipRef.current.wrapperRef?.current;
    if (!el) return;

    let hideToolTipPreviewTimer;
    let tooltipPreviewDelayTimer;
    // Brief delay to confirm the token is genuinely visible (not just passing through during a scroll animation).
    // Was added to prevent false positives where the token briefly appears in view during the auto-scroll after submission
    let visibilityConfirmTimer;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;

        if (visible && !hasAutoShownRef.current) {
          // Don't commit immediately — confirm visibility after a brief pause
          // to avoid false positives from mid-animation scroll states
          visibilityConfirmTimer = setTimeout(() => {
            hasAutoShownRef.current = true;
            tooltipRef.current?.activate();
            hideToolTipPreviewTimer = setTimeout(() => {
              tooltipRef.current?.deactivate();
            }, TOOLTIP_AUTO_SHOW_MS);
          }, 200);
        } else if (!visible) {
          // Token left view — cancel any pending confirmation and hide tooltip
          clearTimeout(visibilityConfirmTimer);
          if (hasAutoShownRef.current) {
            clearTimeout(hideToolTipPreviewTimer);
            tooltipRef.current?.deactivate();
          }
        }
      },
      {threshold: 0, rootMargin: '-100px 0px -100px 0px'}
    );

    // Wait briefly for the post-submission auto-scroll (300ms debounce) to start to avoid triggering on tokens that are momentarily visible
    // before the scroll animation moves them out of view.
    tooltipPreviewDelayTimer = setTimeout(() => {
      observer.observe(el);
    }, 500);

    return () => {
      clearTimeout(tooltipPreviewDelayTimer);
      clearTimeout(hideToolTipPreviewTimer);
      clearTimeout(visibilityConfirmTimer);
      observer.disconnect();
    };
  }, [gradedState]);

  const toggleIsSelected = useCallback(() => {
    onOptionClick(optionId);
  }, [onOptionClick, optionId]);

  const labelClasses = classnames('snippet-selection-option', {
    'snippet-selection-option--selected': isSelected && !isBeingTranslated && !isGraded,
    'snippet-selection-option--focused': hasFocus,
    'pointer-events-auto': gradedState,
    'border-b-2px! border-positive! background-positive-subtle! hover:background-positive-subtle-hover!':
      gradedState === 'correct',
    'border-b-2px! border-negative! background-negative-subtle! hover:background-negative-subtle-hover!':
      gradedState === 'incorrect',
    'border-t-0 border-l-0 border-r-0 border-b-2px! border-negative! border-dashed! hover:background-negative-subtle-hover!':
      gradedState === 'missed'
  });

  const tooltipClasses = useMemo(() => {
    if (!gradedState) return '';
    const caretClass =
      gradedState === 'correct' ? 'snippet-tooltip--correct' : 'snippet-tooltip--negative';
    return classnames(
      caretClass,
      'border-none rounded-3xl! p-x1! text-base! content-primary-inverse-static!',
      {
        'background-positive!': gradedState === 'correct',
        'background-negative!': gradedState !== 'correct'
      }
    );
  }, [gradedState]);

  // Only wrap in WithTooltip when graded
  if (!gradedState) {
    return (
      <label className={labelClasses}>
        <input
          className='snippet-selection-option__hidden-input'
          type='checkbox'
          checked={isSelected}
          onChange={toggleIsSelected}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
        />
        {children}
      </label>
    );
  }

  return (
    <WithTooltip
      ref={tooltipRef}
      as='label'
      content={TOOLTIP_LABELS[gradedState]}
      placement='top'
      enabled
      tooltipClassName={tooltipClasses}
      wrapperProps={{className: labelClasses}}
    >
      <input
        className='snippet-selection-option__hidden-input'
        type='checkbox'
        checked={isSelected}
        onChange={toggleIsSelected}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
      />
      {children}
      <CorrectnessIcon gradedState={gradedState} />
    </WithTooltip>
  );
}

SnippetSelectionOption.propTypes = {
  optionId: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  isExplanation: PropTypes.bool,
  isGuessSubmitted: PropTypes.bool,
  question: PropTypes.instanceOf(getModelForResourceType('question_v3')),
  onOptionClick: PropTypes.func,
  children: PropTypes.node,
  isBeingTranslated: PropTypes.bool
};
